"use client";

import React from "react";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const plans = [{
  id: 1,
  name: "Free",
  monthlyPrice: "$0",
  monthlyPeriod: "",
  annualLine: null,
  description: "For small farms getting started.",
  buttonText: "Get Started Free",
  link: "https://thebarndesk.com/download/",
  isPopular: false
}, {
  id: 2,
  name: "Pro",
  monthlyPrice: "$9.99",
  monthlyPeriod: "/month",
  annualLine: "or $99.99/year — save $19.89",
  description: "For farms that run on their records.",
  buttonText: "Get Pro",
  link: "https://thebarndesk.com/download/",
  isPopular: true
}, {
  id: 3,
  name: "Pro+",
  monthlyPrice: "$19.99",
  monthlyPeriod: "/month",
  annualLine: "or $199/year — save $40.88",
  description: "For farms that need more control.",
  buttonText: "Get Pro+",
  link: "https://thebarndesk.com/download/",
  isPopular: false
}];
const features = [{
  planId: 1,
  text: "Up to 10 animals"
}, {
  planId: 1,
  text: "1 farm"
}, {
  planId: 1,
  text: "20 receipts per month"
}, {
  planId: 1,
  text: "250 MB photo storage"
}, {
  planId: 1,
  text: "Up to 2 family members"
}, {
  planId: 1,
  text: "Animals, breeding & pedigrees, tasks, inventory, equipment"
}, {
  planId: 1,
  text: "Everyday reports: herd inventory, sale sheets, data export"
}, {
  planId: 2,
  text: "Everything on the Free plan"
}, {
  planId: 2,
  text: "Unlimited animals"
}, {
  planId: 2,
  text: "200 receipts per month"
}, {
  planId: 2,
  text: "10 GB photo storage"
}, {
  planId: 2,
  text: "Up to 5 family members"
}, {
  planId: 2,
  text: "Schedule F & Form 4797 tax reports"
}, {
  planId: 2,
  text: "USDA disaster forms, pre-filled from your records"
}, {
  planId: 2,
  text: "Sire performance, weaning + ADG, and cost-per-head reports"
}, {
  planId: 2,
  text: "Herd history & growth comparison"
}, {
  planId: 3,
  text: "Everything on Pro"
}, {
  planId: 3,
  text: "Multiple farms"
}, {
  planId: 3,
  text: "500 receipts per month, per farm"
}, {
  planId: 3,
  text: "50 GB photo storage"
}, {
  planId: 3,
  text: "Up to 15 family members"
}, {
  planId: 3,
  text: "Custom vaccination, kid-care & breeding protocols"
}, {
  planId: 3,
  text: "Priority support"
}];
export default function TailoredInvestmentPlans() {
  const headerReveal = useRevealOnScroll({
    threshold: 0.1
  });
  const gridReveal = useRevealOnScroll({
    threshold: 0.1,
    delay: 200
  } as any);
  return <section data-section-id="221820" id="simple-pricing" className="bg-background text-foreground py-24 px-4 md:px-8 lg:px-16 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div ref={headerReveal.ref} className={`text-center mb-20 transition-all duration-1000 ${headerReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <h2 className="font-default text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight text-primary mb-6">
            Simple <span className="text-primary/80">Pricing</span>
          </h2>
          <p className="text-muted-foreground text-lg md:text-xl max-w-2xl mx-auto font-serif italic">
            Run the farm on any plan. Pro turns your records into money, taxes, and better breeding decisions.
          </p>
        </div>
        <div ref={gridReveal.ref} className={`grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-0 items-center transition-all duration-1000 ${gridReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          {plans.map((plan, i) => {
          const planFeatures = features.filter(f => f.planId === plan.id);
          const isPro = plan.isPopular;
          return <div key={plan.id} data-index={i} className={`relative flex flex-col bg-card rounded-lg transition-all duration-500 ${isPro ? "border-2 border-[#A7C49A] lg:scale-105 z-10 p-10 shadow-xl shadow-primary/5 lg:-mx-4" : "border border-border p-8 z-0"}`}>
                {isPro && <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <Badge className="bg-chart-2 text-primary-foreground px-4 py-1 text-xs font-bold uppercase tracking-widest rounded-md">
                      Most popular
                    </Badge>
                  </div>}
                <div className="mb-8">
                  <h3 className="font-default text-2xl font-bold tracking-tight text-primary mb-2">
                    {plan.name}
                  </h3>
                  <p className="text-muted-foreground text-sm h-12 font-sans">
                    {plan.description}
                  </p>
                </div>
                <div className="mb-8">
                  <div className="flex items-baseline gap-2">
                    <span className="font-default text-5xl md:text-6xl font-bold tracking-tighter text-foreground">
                      {plan.monthlyPrice}
                    </span>
                    <span className="text-muted-foreground font-medium font-sans">
                      {plan.monthlyPeriod}
                    </span>
                  </div>
                  <div className="h-5 mt-2">
                    {plan.annualLine && <p className="text-sm text-chart-2 font-semibold font-sans">
                        {plan.annualLine}
                      </p>}
                  </div>
                </div>
                <ul className="flex flex-col gap-4 mb-10 flex-grow">
                  {planFeatures.map((feature, j) => <li key={j} data-index={j} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-chart-2 shrink-0 mt-0.5" />
                      <span className="text-sm font-medium text-foreground/90 font-sans">
                        {feature.text}
                      </span>
                    </li>)}
                </ul>
                <div className="mt-auto pt-8 border-t border-border/50">
                  <Button asChild variant={isPro ? "default" : "outline"} size="lg" className={`w-full rounded-md font-bold tracking-wide ${isPro ? "bg-primary hover:bg-primary/90 text-primary-foreground" : "hover:bg-accent hover:text-accent-foreground border-border text-primary"}`}>
                    <Link to={plan.link}>{plan.buttonText}</Link>
                  </Button>
                </div>
              </div>;
        })}
        </div>
        <div className="text-center mt-16 space-y-1">
          <p className="text-sm text-muted-foreground font-sans">
            All plans cancel anytime. No contracts. Subscriptions are managed through the Apple App Store or Google Play.
          </p>
          <p className="text-sm text-muted-foreground font-sans">
            Available on iPhone, Android, and the web.
          </p>
        </div>
      </div>
    </section>;
}